import { PagedHistoryRequest } from '../../core/api/paged-history-request';
import { PagedRequest } from './paged-request.model';


export interface OverdraftRequestPreviewModel {
	agentManagerOverdraftOutlet: AgentManagerOverdraftOutlet;
	agentManagerOverdraftGuarantor: AgentManagerOverdraftGuarantor;
	agentManagerOverdraftTOS: AgentManagerOverdraftTOS;
}

export interface AgentManagerForLoanModel {
	content: string[];
	pageable: Pageable;
	last: boolean;
	totalElements: number;
	totalPages: number;
	sort: Sort;
	first: boolean;
	numberOfElements: number;
	size: number;
	number: number;
}

export interface AgentManagerOverdraftGuarantor {
	guarantorOne: Guarantor;
	guarantorTwo: Guarantor;
}

export interface Guarantor {
	firstName: string;
	lastName: string;
	bankVerificationNo: string;
	phoneNumber: string;
}

export interface LoanSuspensionDetails {
	currentCount: number;
	threshold: number;
	suspendFor: number;
	suspensionTimeUnit: string;
	thresholdTimeUnit: string;
	suspensionDueBy: string;
}

export interface LoanNotification {
	contractReference: string;
	recentDefault: string;
	latestSuspensionDueDate: string;
	canTakeLoanOnFreeDays: boolean;
	inMonth: LoanSuspensionDetails;
	inWeek: LoanSuspensionDetails;
	inHour: LoanSuspensionDetails;
	bannerMessage: string;
}

export interface LoanBanner {
	defaultDate: string;
	messageType: string;
	title?: string;
	contractReference: string;
	bannerMessage: string;
}

export const LoanBannerWarningKeyWords = [
	'facility repayment',
	'defaulted'
];

export const LoanBannerErrorKeyWords = [
	'suspended'
];

export const LoanBannerInfoKeyWords = [
	'good job'
];


export interface AgentManagerOverdraftOutlet {
	loanClass: LoanClassModel;
	primaryAgent: PrimaryAgent;
	amountLimit: number;
}

export interface PrimaryAgent {
	id: number;
	businessName: string;
	businessPhone: string;
	code: string;
	bankVerificationNumber: string;
	walletId: number;
	addressLineOne: string;
	addressLineTwo: string;
	localGovernment: LocalGovernment;
	city: string;
}

export interface State {
	id: number;
	createdOn: string;
	lastModifiedOn: string;
	country: LocalGovernment;
	code: string;
	name: string;
	enableWebDeposit: boolean;
	enableWebAirtimePurchase: boolean;
	enableWebBillPayment: boolean;
	status: string;
}

export interface LocalGovernment {
	id: number;
	createdOn: string;
	lastModifiedOn: string;
	state?: State;
	name: string;
	code: string;
	sanefCode?: string;
	status: string;
	dialCode?: string;
}

export interface AgentManagerOverdraftTOS {
	terms: string;
}

export interface LoanClassModel {
	id: number;
	name: string;
	description: string;
	advisedAmount: number;
	existingManagerBusinessName: string;
	hasExistingManager: boolean;
	limit: number;
	qualifyingAmount: number;
	hasActiveLoan: boolean;
	termsAndConditions: string;
	overdraftRequest: OverdraftRequest;
	reapplicationRequest: OverdraftRequest;
	shouldTickApprovalBox: boolean;
	shouldReuploadTos: boolean;
	totalSumOwed: number;
	aggregatorDocumentRequired: boolean;
	requiresGuarantors: boolean;
	type: 'reapplication' | 'request';
	canReapply: boolean;
	eligibleForLoanDisbursement: boolean;
	loanDisbursementErrorMessage: string;
	isPersonalLoan: boolean;
	upgrading: boolean;
	state: any;
	minimumAgentAgeInMonths: any;
	minimumAgentManagerAgeInDays: number;
	agentManagerAgeInDays: number;
	interestRate: number;
	loanRepaymentCycle: string;
	offerValidityInDays: number;
	existingManagerUsername: string;
	available: number;
	activeLoan: boolean;
	existingManagerMessage?: string;
}

export interface OverdraftRequest {
	id: number;
	overdraftRequestStatus: string;
	tatTime: number;
	rejectionReason: null;
	rejectedBy: null;
	status: string;
}


export interface AgentManagerOutletsModel {
	id: number;
	businessName: string;
	businessPhone: string;
	code: string;
	bankVerificationNumber: string;
	walletId: number;
	addressLineOne: string;
	addressLineTwo: string;
	localGovernment: LocalGovernment;
	city: string;
}

export interface LoanContractLimitModel {
	available: number;
	activeLoan: number;
	paymentDueIn: string;
	recoveryDueIn: string;
	totalSumOwed: number;
	interestInCurrentWindow: number;
}


export interface LoanContract {
	approvedLimit: number;
	status: string;
	accountNumber: string;
	interestRate: any;
	totalDrawn: any;
	tenure: any;
	eligibleForLoanDisbursement: boolean;
	loanDisbursementErrorMessage: string;
	nubanAccountNumber: string;
	canUpgrade: boolean;
	permittedToRepay: boolean;
	canReapply: boolean;
	contractReference?:any;
}


export interface LoanContractForAgentManager {
	tenure: string;
	interestRate: number;
	issueDate: string;
	expiryDate: string;
	guarantors: Guarantors;
	loanClass: string;
	code: string;
	overdraftRequestStatus: string;
	status: string;
	activeLoan: number;
}

export class Guarantors {
	guarantorOne: Guarantor;
	guarantorTwo: Guarantor;
}

export interface Guarantor {
	firstName: string;
	lastName: string;
	bankVerificationNo: string;
	phoneNumber: string;
}


export class OverdraftInitiationRequest {
	primaryAgentId: number;
	limit: number;
	loanClassId: number;
}

export class ActivatePndRequestModel {
	comment: string;
}

export enum LoanApprovalStages {
	APPROVED_BY_AGGREGATOR = 'awaiting approval',
	APPROVED_BY_SUPER_AGGREGATOR = 'awaiting approval',
	APPROVED_BY_RISK_OFFICER_ONE = 'awaiting approval',
	UPDATE_APPROVED_BY_RISK_OFFICER_ONE = 'awaiting approval',
	APPROVED = 'active',
	REQUEST_IN_PROGRESS = 'awaiting approval',
	UPDATING_REQUEST = 'awaiting approval',
	UPDATE_REQUEST = 'active',
	AWAITING_CM_APPROVAL = 'awaiting approval',
	DECLINED = 'declined',
	TERMINATED = 'terminated',
}

export interface LoanPaymentsObject {
	totalPages: number;
	totalElements: number;
	pageSize: number;
	pageNumber: number;
	narration: any;
	data: Datum[];
}

export interface Datum {
	reference: string;
	narration: string;
	transactionType: string;
	amount: number;
	currencyCode: string;
	balanceBefore: number;
	balanceAfter: number;
	balanceUpdated: boolean;
	transactionTimestamp: string;
	metadata: string;
}

// Generated by https://quicktype.io

// Generated by https://quicktype.io

export interface LoanTransfersObject {
	content: Content[];
	pageable: Pageable;
	last: boolean;
	totalElements: number;
	totalPages: number;
	sort: Sort;
	first: boolean;
	numberOfElements: number;
	size: number;
	number: number;
}

export interface Content {
	id: number;
	transactionReference: string;
	amount: number;
	status: string;
	shouldBeReversed: boolean;
	reversed: boolean;
	createdOn: string;
	lastModifiedOn: string;
	agentManagerOverdraftContract: AgentManagerOverdraftContract;
	destination: Destination;
	initiatedBy: string;
	approvedBy: string;
	narration: string;
	responseCode: string;
	responseMessage: string;
}

export interface AgentManagerOverdraftContract {
	id: number;
	createdOn: string;
	lastModifiedOn: string;
	agentManager: AgentManager;
	agentManagerOverdraftRequest: AgentManagerOverdraftRequest;
	code: string;
	overdraftReference: string;
	approvedAmount: number;
	overdraftContractSchemeCode: string;
	loanClass: LoanClass;
	loanAccountNumber: string;
	nubanAccountNumber: string;
	cbaCustomerCode: string;
	transitAccountsCreated: boolean;
	tenure: null;
	issueDate: string;
	expiryDate: string;
	interestRate: number;
	interestRateType: string;
	status: string;
}

export interface AgentManager {
	id: number;
	createdOn: string;
	lastModifiedOn: string;
	code: string;
	bank: null;
	businessName: string;
	businessEmail: string;
	businessPhone: string;
	bankAccountNo: null;
	bankAccountName: null;
	activationToken: null;
	wallet: Destination;
	cbaClientId: string;
	cashoutBusinessScheme: null;
	fundWalletBusinessScheme: null;
	nextOfKin: NextOfKin;
	bvn: Bvn;
	passport: string;
	escrowAccountNumber: null;
	bankVerificationNumber: string;
	managerCredentialUsed: null;
	gender: Gender;
	title: Gender;
	webCamPhoto: null;
	backOfficeValidatedBy: string;
	backOfficeValidatedOn: string;
	clusterManagerValidatedBy: null;
	clusterManagerValidatedOn: null;
	pinLastModifiedOn: null;
	passwordLastModifiedOn: null;
	signature: string;
	dateOfBirth: string;
	kycVerificationStatus: string;
	bvnVerificationStatus: string;
	kycVerifiedBy: string;
	kycVerificationRejectionReason: null;
	kycVerifiedOn: string;
	cbaCustomerCode: string;
	completionStatus: string;
	loanCustomerStatus: null;
	backOfficePhotoVerificationStatus: string;
	clusterManagerValidationStatus: string;
	authorizationPinTries: number;
	localGovernment: CashReference;
	addressLineOne: string;
	addressLineTwo: string;
	city: string;
	agentManagerLimitProfile: AgentManagerLimitProfile;
	defaultAgent: null;
	decommissioned: boolean;
	canAccessLoans: boolean;
	status: string;
}

export interface AgentManagerLimitProfile {
	id: number;
	createdOn: string;
	lastModifiedOn: string;
	name: string;
	singleCashoutTransactionLimit: number;
	dailyCashoutLimit: number;
	status: string;
}

export interface Bvn {
	id: number;
	createdOn: string;
	lastModifiedOn: string;
	number: string;
	fullName: string;
	firstName: string;
	lastName: string;
	verificationStatus: string;
	verificationMessage: null | string;
	verifiedBy: null | string;
	verifiedOn: null | string;
	mobileNumber: null | string;
	dateOfBirth: null | string;
	imageRef: null | string;
	status: string;
}

export interface Gender {
	id: number;
	createdOn: string;
	lastModifiedOn: null | string;
	name?: string;
	status: string;
	singleCashoutTransactionLimit?: number;
}

export interface CashReference {
	id: number;
	createdOn: string;
	lastModifiedOn: string;
	state?: State;
	name: string;
	code: string;
	sanefCode?: string;
	status: string;
	dialCode?: string;
	accountId?: number;
	cbnCode?: string;
	nipCode?: string;
}

export interface NextOfKin {
	id: number;
	createdOn: string;
	lastModifiedOn: string;
	fullName: string;
	relationship: string;
	phoneNumber: string;
	emailAddress: string;
	localGovernment: CashReference;
	city: string;
	addressLineOne: string;
	addressLineTwo: null | string;
	status: string;
}

export interface Destination {
	id: number;
	createdOn: string;
	lastModifiedOn: string;
	walletType: string;
	token: string;
	accountId: string;
	accountNumber: string;
	customerCode: string;
	rolezAccountNumber: null | string;
	cashReference: CashReference;
	status: string;
}

export interface AgentManagerOverdraftRequest {
	id: number;
	createdOn: string;
	lastModifiedOn: string;
	agentManager: AgentManager;
	overdraftRequestStatus: string;
	overdraftType: null;
	aggregator: Aggregator;
	loanClass: LoanClass;
	primaryAgent: PrimaryAgent;
	amountLimit: number;
	applicationNumber: string;
	assignedHardLimit: number;
	assignedOverdraftContractSchemeCode: string;
	tatType: string;
	loanAccount: string;
	nubanAccount: string;
	status: string;
}

export interface Aggregator {
	id: number;
	createdOn: string;
	lastModifiedOn: string;
	code: string;
	bank: CashReference;
	businessName: string;
	businessEmail: string;
	businessPhone: string;
	bankAccountNo: string;
	bankAccountName: string;
	activationToken: string;
	wallet: Destination;
	cbaClientId: string;
	cashoutBusinessScheme: TBusinessScheme;
	fundWalletBusinessScheme: TBusinessScheme;
	nextOfKin: null;
	bvn: Bvn;
	passport: string;
	escrowAccountNumber: string;
	bankVerificationNo: string;
	addressLineOne: string;
	addressLineTwo: string;
	city: string;
	kycVerificationStatus: string;
	kycVerifiedBy: string;
	kycVerificationRejectionReason: null;
	superAggregator: SuperAggregator;
	localGovernment: CashReference;
	aggregatorLimitProfile: Gender;
	aggregatorCommission: AggregatorCommission;
	displayAccountBalance: boolean;
	documents: Document[];
	status: string;
}

export interface AggregatorCommission {
	id: number;
	createdOn: string;
	lastModifiedOn: string;
	code: string;
	withdrawalCommission: Commission;
	depositCommission: Commission;
	airtimeCommission: Commission;
	billPaymentCommission: Commission;
}

export interface Commission {
	id: number;
	createdOn: string;
	lastModifiedOn: null | string;
	name: string;
	transactionType: string;
	chargeScheme: any;
	chargePercentage: number | null;
	chargeAmount: number | null;
	capAmount: null;
	minimumAmount: number | null;
}

export interface TBusinessScheme {
	id: number;
	createdOn: any;
	lastModifiedOn: string;
	name: string;
	code: string;
	enableBatchPosting: boolean;
}

export interface Document {
	id: number;
	createdOn: string;
	lastModifiedOn: string;
	documentType: number;
	contentType: string;
	defaultDocument: number;
	status: string;
	reference?: string;
}

export interface SuperAggregator {
	id: number;
	createdOn: string;
	lastModifiedOn: string;
	code: string;
	bank: CashReference;
	businessName: string;
	businessEmail: string;
	businessPhone: string;
	bankAccountNo: string;
	bankAccountName: null;
	activationToken: string;
	wallet: Destination;
	cbaClientId: string;
	cashoutBusinessScheme: TBusinessScheme;
	fundWalletBusinessScheme: TBusinessScheme;
	nextOfKin: NextOfKin;
	bvn: Bvn;
	passport: string;
	escrowAccountNumber: string;
	monnifyUser: MonnifyUser;
	bankVerificationNo: string;
	addressLineOne: string;
	addressLineTwo: string;
	city: string;
	kycVerificationStatus: string;
	kycVerifiedBy: string;
	kycVerificationRejectionReason: null;
	localGovernment: CashReference;
	supremeAggregator: SupremeAggregator;
	superAggregatorLimitProfile: Gender;
	aggregatorCommission: AggregatorCommission;
	displayAccountBalance: boolean;
	documents: Document[];
	status: string;
}

export interface MonnifyUser {
	id: number;
	createdOn: string;
	lastModifiedOn: string;
	username: string;
	firstName: string;
	middleName: null;
	lastName: string;
	emailAddress: string;
	mobileNumber: string;
	roles: null;
	authorities: null;
	tokenData: null;
	status: string;
}

export interface SupremeAggregator {
	id: number;
	createdOn: string;
	lastModifiedOn: string;
	code: string;
	bank: CashReference;
	businessName: string;
	businessEmail: string;
	businessPhone: string;
	bankAccountNo: string;
	bankAccountName: null;
	activationToken: string;
	wallet: Destination;
	cbaClientId: string;
	cashoutBusinessScheme: TBusinessScheme;
	fundWalletBusinessScheme: TBusinessScheme;
	nextOfKin: null;
	bvn: null;
	passport: null;
	escrowAccountNumber: null;
	monnifyUser: MonnifyUser;
	bankVerificationNo: string;
	addressLineOne: string;
	addressLineTwo: string;
	city: string;
	localGovernment: CashReference;
	aggregatorCommission: AggregatorCommission;
	displayAccountBalance: boolean;
	status: string;
}

export interface LoanClass {
	id: number;
	createdOn: string;
	lastModifiedOn: string;
	name: string;
	limitCalculationRange: number;
	tatPeriod: number;
	percentageOnBalance: number;
	description: string;
	maximumCap: number;
	termsAndConditions: string;
	shouldTickApprovalBox: boolean;
	shouldReuploadTos: boolean;
	aggregatorDocumentRequired: boolean;
	qualifyingAmount: number;
	status: string;
}

export interface PrimaryAgent {
	id: number;
	createdOn: string;
	lastModifiedOn: string;
	code: string;
	bank: CashReference;
	businessName: string;
	businessEmail: string;
	businessPhone: string;
	bankAccountNo: string;
	bankAccountName: string;
	activationToken: string;
	wallet: Destination;
	cbaClientId: string;
	cashoutBusinessScheme: TBusinessScheme;
	fundWalletBusinessScheme: TBusinessScheme;
	nextOfKin: NextOfKin;
	bvn: Bvn;
	passport: string;
	escrowAccountNumber: null;
	aggregator: Aggregator;
	bankVerificationNo: string;
	authorizationPinTries: number;
	addressLineOne: string;
	addressLineTwo: string;
	city: string;
	agentLimitProfile: AgentLimitProfile;
	localGovernment: LocalGovernment;
	dateOfBirth: string;
	gender: string;
	meansOfIdentification: string;
	idNumber: string;
	issueDate: string;
	expirationDate: string;
	occupation: string;
	jobTitle: string;
	mothersMaidenName: string;
	kycVerificationStatus: string;
	kycVerifiedBy: string;
	kycVerificationRejectionReason: null;
	pinLastModifiedOn: null;
	passwordLastModifiedOn: null;
	terminalAssignedOn: null;
	sanefCode: null;
	onboardingMode: string;
	enabledProducts: any;
	printMerchantCopy: boolean;
	defaultWithdrawalBusinessScheme: null;
	defaultDepositBusinessScheme: null;
	agentProtectedTransferBusinessScheme: null;
	registeredOnSanef: boolean;
	documents: Document[];
	status: string;
}

export interface AgentLimitProfile {
	id: number;
	createdOn: string;
	lastModifiedOn: string;
	singleWithdrawalTransactionLimit: number;
	singleDepositTransactionLimit: number;
	singleCashoutTransactionLimit: number;
	singleAirtimeTransactionLimit: number;
	singleBillPaymentTransactionLimit: number;
	dailyWebDepositTransactionLimit: number;
	dailyPosDepositTransactionLimit: number;
	dailyMobileDepositTransactionLimit: number;
	dailyWebCashoutTransactionLimit: number;
	dailyPosCashoutTransactionLimit: number;
	dailyMobileCashoutTransactionLimit: number;
	dailyWebAirtimeTransactionLimit: number;
	dailyMobileAirtimeTransactionLimit: number;
	dailyPosAirtimeTransactionLimit: number;
	dailyWebBillPaymentTransactionLimit: number;
	dailyMobileBillPaymentTransactionLimit: number;
	dailyPosBillPaymentTransactionLimit: number;
	dailyCumulativeAirtimeTransactionLimit: number;
	dailyCumulativeBillPaymentTransactionLimit: number;
	dailyCumulativeDepositTransactionLimit: number;
	dailyCumulativeCashoutTransactionLimit: number;
	dailyCumulativeWithdrawalTransactionLimit: number;
	singleProtectedTransferTransactionLimit: number;
	dailyCumulativeProtectedTransferTransactionLimit: number;
	name: string;
	code: string;
	status: string;
}

export interface Pageable {
	sort: Sort;
	pageNumber: number;
	pageSize: number;
	offset: number;
	paged: boolean;
	unpaged: boolean;
}

export interface Sort {
	sorted: boolean;
	unsorted: boolean;
}

// Generated by https://quicktype.io

export class LoanTransferRequest {
	narration = 'Loan Fund Transfer';
	amount: number;
	destinationWalletId: number;
	agentPin: string;
	loanClassId: number;
	uniqueIdentifier: string;
}

export class LoanNotificationTriggerRequest {
	loanContractReference: string
}

// Generated by https://quicktype.io

export class LoanRepaymentRequest {
	narration = 'Loan Payment';
	amount: number;
	sourceWalletId: number;
	loanClassId: number;
	agentPin: string;
	uniqueIdentifier: string;
}

export class LoanUpdateRequest {
	primaryAgentId: number;
	guarantorTwoBankVerificationNo: string;
	guarantorTwoFirstName: string;
	guarantorTwoLastName: string;
	guarantorTwoPhoneNumber: string;
	guarantorOnePhoneNumber: string;
	guarantorOneBankVerificationNo: string;
	guarantorOneLastName: string;
	guarantorOneFirstName: string;
	loanClassId: number;
	limit: number;
	conditionDocument: any;
}

// Generated by https://quicktype.io

export interface LoanContractsDetails {
	tenure: string;
	interestRate: number;
	issueDate: string;
	expiryDate: string;
	guarantors: Guarantors;
	loanClass: string;
	code: string;
	overdraftRequestStatus: string;
	status: string;
	activeLoan: number;
}

// Generated by https://quicktype.io

export interface LoanContractHistory {
	totalPages: number;
	totalItems: number;
	content: InterestPaymentItems[];
}

export interface InterestPaymentItems {
	startTime: string;
	endTime: string;
	totalDrawn: number;
	totalRepaid: number;
	interest: number;
}

export type LoanActionType = 'transfer' | 'repay' | 'repay_all';

// Generated by https://quicktype.io

export interface LoanRequestModel {
	content: LoanRequestList[];
	totalElements: number;
	totalPages: number;
}

export interface LoanRequestList {
	id: number;
	loanClass: string;
	managerName: string;
	applicationNumber: string;
	aggregatorName?: string;
	createdOn: string;
}

// Generated by https://quicktype.io

export interface LoanEligibilityModel {
	belongsToGroup: boolean;
	groupHasReachedQuorum: boolean;
	eligibleToJoinGroup: boolean;
	eligibleToCreateGroup: boolean;
}

// Generated by https://quicktype.io

export interface LoanContractsRequestModel {
	content: LoanContractsRequestList[];
	pageable: Pageable;
	totalElements: number;
	totalPages: number;
	last: boolean;
	sort: Sort;
	first: boolean;
	numberOfElements: number;
	size: number;
	number: number;
}

export interface LoanContractsRequestList {
	agentManager: string;
	aggregator: null;
	loanAccountNumber: string;
	approvedLimit: number;
	loanClass: string;
	reference: string;
	loanContractState: string;
	pndActive: boolean;
}

// Generated by https://quicktype.io

export interface LoanTransactionsModel {
	content: TransactionContent[];
	pageable: Pageable;
	last: boolean;
	totalElements: number;
	totalPages: number;
	sort: Sort;
	first: boolean;
	numberOfElements: number;
	size: number;
	number: number;
}

export interface TransactionContent {
	interestAmount: number;
	principalAmount: number;
	createdOn: string;
	earning: null;
	agentManagerBusinessName: string;
	contractReference: string;
}

export interface Loans {
	loanDescription: string;
	loanState: string;
}

export interface LoanGroupMembersDto {
	content: LoanGroupMembers[];
	totalElements: number;
	totalPages: number;
	numberOfElements: number;
	size: number;
	number: number;
}

export interface LoanGroupMembers {
	name: string;
	role: string;
	dateJoined: string;
	loans: Loans[];
}

export interface LoanGroupDetails {
	name: string;
	groupAdmin: string;
	code: string;
	groupManager: string;
	memberCount: number;
	dateCreated: string;
}

export interface LoanCandidatesSummary {
	belongsToGroup: boolean;
	groupHasReachedQuorum: boolean;
	eligibleToJoinGroup: boolean;
	eligibleToCreateGroup: boolean;
}

export enum LoanDetailType {
	repayment = 'repayment',
	interestPayment = 'interest payment',
	disbursement = 'disbursement',
}

export enum ViewType {
	MOBILE = 'MOBILE',
	WEB = 'WEB'
}
// Generated by https://quicktype.io

export interface LoanRepaymentDetailModel {
	status: string;
	message: string;
	transaction: LoanRepaymentTransaction;
	debitEntries: ItEntry[];
	creditEntries: ItEntry[];
	successful: boolean;
}

export interface ItEntry {
	amount: number;
	accountName: string;
	accountNumber: string;
	accountNuban: string;
}

export interface LoanRepaymentTransaction {
	reference: string;
	narration: string;
	amount: number;
	minorAmount: number;
	currencyCode: string;
	comment: string;
	metadata: string;
}

// Generated by https://quicktype.io

export interface OverdraftFAQModel {
	id: number;
	createdOn: string;
	lastModifiedOn: string;
	question: string;
	answer: string;
	status: string;
}

export class LoanDataRequestModel extends PagedRequest {
	agentManager: string;
	timeFilter: any;
	loanState: LoanDataState;

	constructor(obj?) {
		super(obj);
		this.agentManager = (obj && obj.agentManager) || '';
		this.timeFilter = (obj && obj.timeFilter) || '';
		this.loanState = (obj && obj.loanState) || '';
	}
}

export class LoanContractRequestParams extends PagedRequest {
	agentManager: string;

	constructor(obj?) {
		super(obj);
		this.agentManager = (obj && obj.agentManager) || '';
	}
}

export class AgentManagersForLoansRequestModel extends PagedRequest {
	searchParam: string;

	constructor(obj?) {
		super(obj);
		this.searchParam = (obj && obj.searchParam) || '';
	}
}

// Generated by https://quicktype.io

export interface LoanDataListModel {
	content: LoanDataListContent[];
	pageable: Pageable;
	last: boolean;
	totalElements: number;
	totalPages: number;
	sort: Sort;
	first: boolean;
	numberOfElements: number;
	size: number;
	number: number;
}

export interface LoanDataListContent {
	agentManagerName: string;
	agentManagerPhoneNumber: string;
	balanceMinor: number;
	approvedLimitMinor: number;
	status: string;
	aggregatorName: string;
	aggregatorPhoneNumber: string;
	hoursToRecovery: number;
	hoursToDefault: number;
	daysInRecovery: number;
}

// Generated by https://quicktype.io

export interface LoanDataSummaryModel {
	approvedLoanValueMinor: number;
	activeLoanValueMinor: number;
	activeLoanCount: number;
	defaultLoanValueMinor: number;
	defaultLoanCount: number;
	recoveryLoanValueMinor: number;
	recoveryLoanCount: number;
	nonPerformingLoans: number;
	defaultNonPerformingLoans: number;
	recoveredNonPerformingLoans: number;
}

export const enum LoanDataState {
	ACTIVE = 'ACTIVE',
	DEFAULT = 'DEFAULT',
	RECOVERY = 'RECOVERY',
}

export interface ResidentialAddress {
	lineOne: string;
	lineTwo: string;
	city: string;
	lga: string;
	state: string;
	country?: string;
}

export interface Collateral {
	collateralId: null;
	collateralInformation: CollateralInformation;
}

export interface CollateralInformation {
	type: string;
	value: string;
	owner: string;
	ownerBvn: string;
	location: ResidentialAddress;
	customFields?: any;
}

export interface Tor {
	bvn: string;
	phone: string;
	email: string;
	residentialAddress: ResidentialAddress;
	occupation: string;
	workAddress: ResidentialAddress;
	relationshipId: string;
}

export interface LoanInformation {
	businessOwnerId: number;
	moniepointLoanProgramId: number;
	loanAmount: number;
	loanDuration: number;
	disbursementAccount: string;
	repaymentAccount: string;
}

export interface BusinessLoanModel extends LoanInformation {
	guarantors: Tor[];
	codebtors: Tor[];
	collaterals: Collateral[];
}

export interface LoanProgram {
	defaultInterestRateInPercentage: number;
	id: number;
	isCollateralRequired: boolean;
	loanProduct: string;
	maximumAmount: number;
	maximumTenure: number;
	minNoOfGuarantors: number;
	minimumAmount: number;
	minimumTenure: number;
	moratoriumInDays: number;
	name: string;
	paymentFrequency: number;
	paymentFrequencyUnit: string;
	tenureUnit: string;
}

export type CollateralOwners = 'CUSTOMER' | 'GUARANTOR' | 'CODEBTOR';
export type CollateralTypes = 'BUILDING' | 'LAND' | 'VEHICLE' | 'FIXED_ASSET';


export interface LoanOverdraftGrowthConfig {
	ageMinimum: number
	enable: boolean
	hasContract: string
	kyc: string
	minAvgBalanceCriteria: number
}
export class ConsolidatedBRMLoanPayoutWalletTransactionRequestModel extends PagedHistoryRequest {
	amount = 0;
	reference: string;
	transactionType: string;

	constructor(obj?) {
		super(obj);
		this.amount = obj && obj.amount || 0;
		this.reference = obj && obj.reference || '';
		this.transactionType = obj && obj.transactionType || '';
	}
}

export class ConsolidatedPortfolioManagementRequestModel extends PagedHistoryRequest {
	
	contractState: string;
	businessOwnerName: string;
	businessName: string;
	brmCode: string;
	scCode: string


	constructor(obj?) {
		super(obj);
		this.contractState = obj && obj.contractState || 0;
		this.businessOwnerName = obj && obj.businessOwnerName || '';
		this.businessName = obj && obj.businessOwner || '';
		this.brmCode = obj && obj.brmCode || "";
		this.scCode = obj && obj.scCode || "";
	}
}

export class ConsolidatedLoanRequestListModel extends PagedHistoryRequest {
	businessOwnerName: any;
	businessName: any;
	loanStatus: any;
	brmCode: string;
	scCode: string;
	sortBy: string;

	constructor(obj?) {
		super(obj);
		this.loanStatus = obj && obj.loanStatus || '';
		this.businessOwnerName = obj && obj.businessOwnerName || '';
		this.businessName = obj && obj.businessOwner || '';
		this.brmCode = obj && obj.brmCode || ''
		this.scCode = obj && obj.scCode || ''
		this.sortBy = obj && obj.sortBy || ''
	}
}